class NotificationSound {
  constructor() {
    this.audioContext = null;
    this.sounds = {};
    this.isEnabled = true;
    this.volume = 0.5;
  }

  // Initialize audio context
  init() {
    if (typeof window !== 'undefined' && window.AudioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }

  // Set volume (0-1)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  // Create a simple notification sound using Web Audio API
  createNotificationSound(frequency = 800, duration = 200) {
    if (!this.isEnabled || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(this.volume * 0.3, this.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration / 1000);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration / 1000);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }

  // Play different sounds based on notification priority
  playNotificationSound(priority = 'low') {
    if (!this.isEnabled) return;

    // Resume audio context if suspended (required by browsers)
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    switch (priority) {
      case 'high':
        // High priority: Two-tone alert
        this.createNotificationSound(1000, 150);
        setTimeout(() => this.createNotificationSound(800, 150), 200);
        break;
      case 'medium':
        // Medium priority: Single higher tone
        this.createNotificationSound(900, 200);
        break;
      case 'low':
      default:
        // Low priority: Gentle single tone
        this.createNotificationSound(800, 200);
        break;
    }
  }

  // Play sound for specific notification types
  playTypeSound(type) {
    if (!this.isEnabled) return;

    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    switch (type) {
      case 'new_offer':
        // Money sound (higher frequency)
        this.createNotificationSound(1200, 300);
        break;
      case 'new_message':
        // Message sound (medium frequency)
        this.createNotificationSound(900, 250);
        break;
      case 'vehicle_update':
        // Update sound (lower frequency)
        this.createNotificationSound(600, 200);
        break;
      case 'new_schedule':
        // Schedule sound (ascending tone)
        this.createNotificationSound(600, 100);
        setTimeout(() => this.createNotificationSound(800, 100), 120);
        break;
      case 'system_notification':
        // System sound (alert tone)
        this.createNotificationSound(1000, 150);
        setTimeout(() => this.createNotificationSound(800, 150), 200);
        break;
      default:
        this.createNotificationSound(800, 200);
        break;
    }
  }

  // Cleanup
  destroy() {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Create singleton instance
const notificationSound = new NotificationSound();

// Initialize on first use
if (typeof window !== 'undefined') {
  notificationSound.init();
}

export default notificationSound; 